import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, X, ArrowRightToLine, ArrowLeftToLine, Calendar } from "lucide-react";
import JobApplicationClient from "../data/api/JobApplicationClient";
import type { JobApplication } from "../data/api/JobApplicationClient";
import JobOpportunityClient from "../../jobOpportunity/data/api/JobOpportunityClient";
import Loader from "../../../shared/components/Loader";
import ApplicationCard from "../components/ApplicationCard";
import AddApplicantModal from "../components/AddApplicantModal";
import RejectModal from "../components/RejectModal";
import MatchBreakdownModal from "../components/MatchBreakdownModal";
import { ProspectiveCandidatesBanner } from "../components/ProspectiveCandidatesBanner";
import { ProspectiveCandidatesColumn } from "../components/ProspectiveCandidatesColumn";
import { useProspectiveCandidates } from "../hooks/useProspectiveCandidates";
import "../JobApplications.css";

const DEFAULT_STAGES = ["Applied", "Shortlisted", "Interviewed", "Offered", "Accepted", "Declined", "Rejected"];

const STAGE_COLOR_CLASS: Record<string, string> = {
  Applied: "applied",
  Screening: "screening",
  Interview: "interview",
  Offer: "offer",
  Hired: "hired",
  Rejected: "rejected",
};

const getStageClass = (stage: string) => STAGE_COLOR_CLASS[stage] || "applied";

interface JobApplicationsKanbanPageProps {
  isEmbedded?: boolean;
}

const JobApplicationsKanbanPage: React.FC<JobApplicationsKanbanPageProps> = ({ isEmbedded = false }) => {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const opportunityTitle = searchParams.get("title") || "Opportunity";

  const [stages, setStages] = useState<string[]>(DEFAULT_STAGES);
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<{ app: JobApplication, action: "Rejected" | "Declined" } | null>(null);
  const [bulkRejectTarget, setBulkRejectTarget] = useState<string | null>(null);
  const [breakdownApp, setBreakdownApp] = useState<JobApplication | null>(null);
  const [prospectiveCollapsed, setProspectiveCollapsed] = useState(false);

  // ── Define loadData FIRST so the prospective callback is never stale ──────
  const loadData = useCallback(async () => {
    if (!opportunityId) return;
    try {
      const oppData = await JobOpportunityClient.getOpportunity(opportunityId);
      if (oppData.stages && oppData.stages.length > 0) {
        setStages(oppData.stages);
      }
      const data = await JobApplicationClient.getApplicants(opportunityId);
      setApplications(data.records || []);
    } catch (err: any) {
      setError(err?.message || "Failed to load applicants.");
    }
  }, [opportunityId]);

  // Stable callback passed to the prospective hook — always calls latest loadData
  const onCandidateAdded = useCallback(() => { loadData(); }, [loadData]);

  const prospectiveData = useProspectiveCandidates(opportunityId, onCandidateAdded);

  const [draggingApp, setDraggingApp] = useState<JobApplication | null>(null);
  const [hoverStage, setHoverStage] = useState<string | null>(null);
  const [moving, setMoving] = useState<Record<string, boolean>>({});
  const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set());

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [isBulkDropdownOpen, setIsBulkDropdownOpen] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    loadData().finally(() => setLoading(false));
  }, [loadData]);


  const grouped = useMemo(() => {
    const map: Record<string, JobApplication[]> = {};
    stages.forEach((s) => (map[s] = []));
    applications.forEach((app) => {
      const key = app.stage || "Applied";
      if (map[key]) map[key].push(app);
      else map[key] = [app];
    });
    return map;
  }, [applications, stages]);

  const moveApplication = useCallback(
    async (app: JobApplication, targetStage: string) => {
      if (app.stage === targetStage || !opportunityId || !app.candidate_id) return;
      
      if (targetStage === "Rejected" || targetStage === "Declined") {
        setRejectTarget({ app, action: targetStage });
        return;
      }

      setMoving((prev) => ({ ...prev, [app.application_id || app.candidate_id || ""]: true }));
      try {
        const updated = await JobApplicationClient.moveStage(opportunityId, app.candidate_id, targetStage);
        setApplications((prev) =>
          prev.map((a) => (a.application_id === app.application_id ? { ...a, ...updated, stage: targetStage } : a))
        );
        await loadData();
      } catch (err: any) {
        setError(err?.message || "Failed to move applicant.");
      } finally {
        setMoving((prev) => {
          const next = { ...prev };
          delete next[app.application_id || app.candidate_id || ""];
          return next;
        });
      }
    },
    [opportunityId, loadData]
  );

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, app: JobApplication) => {
    setDraggingApp(app);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", app.application_id || app.candidate_id || "");
    setTimeout(() => { (e.target as HTMLElement).classList.add("dragging"); }, 0);
  };

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    (e.target as HTMLElement).classList.remove("dragging");
    setDraggingApp(null);
    setHoverStage(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetStage: string) => {
    e.preventDefault();
    setHoverStage(null);
    if (draggingApp) {
      moveApplication(draggingApp, targetStage);
    }
  };

  const handleBulkMove = async (targetStage: string) => {
    setIsBulkDropdownOpen(false);
    if (!targetStage || selectedIds.length === 0 || !opportunityId) return;
    
    if (targetStage === "Rejected" || targetStage === "Declined") {
      setBulkRejectTarget(targetStage);
      return;
    }

    setIsBulkUpdating(true);
    try {
      const candidateIds = applications
        .filter(app => selectedIds.includes(app.application_id! || app.candidate_id!))
        .map(app => app.candidate_id!);
        
      await JobApplicationClient.bulkMoveStage(opportunityId, candidateIds, targetStage);
      await loadData();
      setSelectedIds([]);
    } catch (err: any) {
      setError(err?.message || "Failed to bulk move applicants.");
    } finally {
      setIsBulkUpdating(false);
    }
  };

  const handleBulkScheduleInterview = useCallback(() => {
    if (selectedIds.length === 0 || !opportunityId) return;
    const emails = applications
      .filter(app => selectedIds.includes(app.application_id! || app.candidate_id!))
      .map(app => app.candidate_email)
      .filter(Boolean) as string[];
    if (emails.length === 0) {
      setError("No email found for selected applicant(s).");
      return;
    }
    setIsBulkDropdownOpen(false);
    navigate('/dashboard/interviews/schedule', {
      state: {
        selectedEmails: emails,
        jobOpportunityId: opportunityId,
        returnUrl: window.location.pathname + window.location.search
      }
    });
  }, [selectedIds, applications, opportunityId, navigate]);

  const toggleCollapse = useCallback((stage: string) => {
    setCollapsedStages(prev => {
      const next = new Set(prev);
      if (next.has(stage)) next.delete(stage);
      else next.add(stage);
      return next;
    });
  }, []);

  const toggleSelect = (app: JobApplication) => {
    const id = app.application_id || app.candidate_id || "";
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleMoveToStage = useCallback((app: JobApplication, direction: "PREV" | "NEXT") => {
    const idx = stages.indexOf(app.stage);
    if (idx === -1) return;
    const targetIdx = direction === "PREV" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= stages.length) return;
    moveApplication(app, stages[targetIdx]);
  }, [stages, moveApplication]);

  const handleAddApplicant = useCallback(async (formData: FormData) => {
    if (!opportunityId) return;
    await JobApplicationClient.addApplicant(opportunityId, formData);
    await loadData();
  }, [opportunityId, loadData]);

  const handleRejectConfirm = useCallback(
    async (reasonType: string, customReason?: string) => {
      if (!opportunityId || !rejectTarget || !rejectTarget.app.candidate_id) return;
      if (rejectTarget.action === "Declined") {
        await JobApplicationClient.declineApplicant(opportunityId, rejectTarget.app.candidate_id, reasonType, customReason);
      } else {
        await JobApplicationClient.rejectApplicant(opportunityId, rejectTarget.app.candidate_id, reasonType, customReason);
      }
      setRejectTarget(null);
      await loadData();
    },
    [opportunityId, rejectTarget, loadData]
  );

  const handleBulkRejectConfirm = useCallback(
    async (reasonType: string, customReason?: string) => {
      if (!opportunityId || !bulkRejectTarget || selectedIds.length === 0) return;
      setIsBulkUpdating(true);
      try {
        const candidateIds = applications
          .filter(app => selectedIds.includes(app.application_id! || app.candidate_id!))
          .map(app => app.candidate_id!);
          
        await JobApplicationClient.bulkMoveStage(opportunityId, candidateIds, bulkRejectTarget, reasonType, customReason);
        await loadData();
        setSelectedIds([]);
      } catch (err: any) {
        setError(err?.message || "Failed to bulk move applicants.");
      } finally {
        setIsBulkUpdating(false);
        setBulkRejectTarget(null);
      }
    },
    [opportunityId, bulkRejectTarget, selectedIds, applications, loadData]
  );

  const handleOpenResume = useCallback((url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  if (loading) return <Loader isLoading={true} message="Loading applicants..." />;

  const isUpdating = Object.values(moving).some(Boolean) || isBulkUpdating;

  return (
    <div className={`kan-page${!isEmbedded ? " kan-page--standalone" : ""}`}>
      <Loader isLoading={isUpdating} message="Updating stages..." />
      {selectedIds.length > 0 && (
        <div className="kan-bulk-bar">
          <span className="kan-bulk-text">{selectedIds.length} item(s) selected</span>
          <div className="kan-bulk-dropdown-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>

            <button 
              className="kan-bulk-select" 
              onClick={() => setIsBulkDropdownOpen(!isBulkDropdownOpen)} 
              disabled={isBulkUpdating}
            >
              Move to stage...
            </button>
            <button
              className="kan-bulk-select kan-bulk-schedule-btn"
              onClick={handleBulkScheduleInterview}
              disabled={isBulkUpdating}
              title="Schedule interview for selected applicant(s)"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            >
              <Calendar size={14} />
              Schedule Interview
            </button>
            {isBulkDropdownOpen && (
              <div className="kan-bulk-dropdown-menu">
                {stages.map(s => (
                  <div key={s} className="kan-bulk-dropdown-item" onClick={() => handleBulkMove(s)}>
                    {s}
                  </div>
                ))}
              </div>
            )}
          </div>
          <button className="kan-bulk-close-btn" onClick={() => setSelectedIds([])} title="Clear selection">
            <X size={16} />
          </button>
        </div>
      )}

      {!isEmbedded && (
        <div className="kan-header">
          <div className="kan-header-left">
            <button className="kan-back-btn" onClick={() => navigate("/dashboard/job-opportunities")} title="Back">
              <ArrowLeft size={18} />
            </button>
            <h1 className="kan-page-title">Applicants – {opportunityTitle}</h1>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {error && <span style={{ color: "#dc2626", fontSize: "0.8rem" }}>{error}</span>}
          </div>
        </div>
      )}
      
      {isEmbedded && error && (
        <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "16px", marginTop: "-16px" }}>
          <span style={{ color: "#dc2626", fontSize: "0.8rem", marginRight: "12px", alignSelf: "center" }}>{error}</span>
        </div>
      )}

      {/* ── Prospective Candidates Swimlane ── */}
      {opportunityId && (prospectiveData.hasResults || prospectiveData.loading || prospectiveData.isProcessing) && (
        <ProspectiveCandidatesBanner
          collapsed={prospectiveCollapsed}
          onToggleCollapse={() => setProspectiveCollapsed(!prospectiveCollapsed)}
          candidateCount={prospectiveData.candidates.length}
        />
      )}

      <div className="kan-board">
        {opportunityId && (prospectiveData.hasResults || prospectiveData.loading || prospectiveData.isProcessing) && (
          <ProspectiveCandidatesColumn
            opportunityId={opportunityId}
            candidates={prospectiveData.candidates}
            addingId={prospectiveData.addingId}
            onAdd={prospectiveData.handleAdd}
            collapsed={prospectiveCollapsed}
          />
        )}
        {stages.map((stage, stageIndex) => {
          const cards = grouped[stage] || [];
          const isCollapsed = collapsedStages.has(stage);

          if (isCollapsed) {
            return (
              <div key={stage} className="kan-column kan-column-collapsed">
                <div className="kan-collapsed-header" onClick={() => toggleCollapse(stage)}>
                  <ArrowRightToLine size={16} className="kan-expand-icon" />
                  <div className="kan-collapsed-title">
                    <span className={`kan-stage-dot ${getStageClass(stage)}`} />
                    {stage} ({cards.length})
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div key={stage} className="kan-column">
              <div className="kan-column-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="kan-column-title">
                    <span className={`kan-stage-dot ${getStageClass(stage)}`} />
                    {stage}
                  </span>
                  <span className="kan-column-count">{cards.length}</span>
                </div>
                <div className="kan-column-menu-wrapper" style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <button 
                    className="kan-column-menu-btn" 
                    onClick={() => toggleCollapse(stage)}
                    title="Collapse Status"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b', padding: '4px', borderRadius: '4px' }}
                  >
                    <ArrowLeftToLine size={16} />
                  </button>
                </div>
              </div>
              <div 
                className={`kan-column-body drop-zone ${hoverStage === stage ? "over" : ""}`}
                onDragOver={(e) => { e.preventDefault(); setHoverStage(stage); }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setHoverStage(null); }}
                onDrop={(e) => handleDrop(e, stage)}
              >
                {cards.map((app) => {
                  const appKey = app.application_id || app.candidate_id || `${stage}-${Math.random()}`;
                  return (
                    <ApplicationCard
                      key={appKey}
                      app={app}
                      columnStage={stage}
                      isProcessing={!!moving[appKey]}
                      stageIndex={stageIndex}
                      totalStages={stages.length}
                      isSelected={selectedIds.includes(appKey)}
                      onToggleSelect={toggleSelect}
                      onDragStart={handleDragStart}
                      onDragEnd={handleDragEnd}
                      onOpenResume={handleOpenResume}
                      onReject={(a) => setRejectTarget({ app: a, action: "Rejected" })}
                      onMoveToStage={handleMoveToStage}
                      onClickMatchScore={(a) => setBreakdownApp(a)}
                    />
                  );
                })}
                {cards.length === 0 && (
                  <div className="kan-empty-col">
                    <Users size={14} /> No applicants
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <AddApplicantModal open={addModalOpen} title={opportunityTitle} onClose={() => setAddModalOpen(false)} onSubmit={handleAddApplicant} />
      {rejectTarget && (
        <RejectModal
          open={true}
          title={rejectTarget.action === "Declined" ? "Decline Applicant" : "Reject Applicant"}
          candidateName={rejectTarget.app.candidate_name || ""}
          onClose={() => setRejectTarget(null)}
          onConfirm={handleRejectConfirm}
        />
      )}
      {bulkRejectTarget && (
        <RejectModal
          open={true}
          title={`Bulk ${bulkRejectTarget}`}
          candidateName={`${selectedIds.length} applicants`}
          onClose={() => setBulkRejectTarget(null)}
          onConfirm={handleBulkRejectConfirm}
        />
      )}
      <MatchBreakdownModal open={!!breakdownApp} candidateName={breakdownApp?.candidate_name || ""} opportunityTitle={opportunityTitle} resumeUrl={breakdownApp?.resume_url} matchBreakdown={breakdownApp?.match_breakdown} matchScore={breakdownApp?.match_score} candidateId={breakdownApp?.candidate_id} onClose={() => setBreakdownApp(null)} />

    </div>
  );
};

export default JobApplicationsKanbanPage;
